import React, { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
    Paper,
    Grid,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Typography,
    Box,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import useNotification from "../../common/Notification";
import { WB_SX } from "../../../ui/ux/uiTokens";
import HealthBadge from "../shared/HealthBadge";
import SubscriptionFields from "../shared/SubscriptionFields";
import {
    getCredits,
    updateCredits,
    getUserSubscriptions,
    updateSubscription,
    getUserServiceStatus,
    updateUserRoles,
} from "../../../api/superAdminApi";
import { parseUserRoles } from "../../UserManagement/roleLabels";

const AdminPersonalWhatsappPage = () => {
    const { user, reloadUser } = useOutletContext();
    const notify = useNotification();
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [credits, setCredits] = useState({ pwc: "", dwpc: "", adwpc: "" });
    const [subscriptions, setSubscriptions] = useState(null);
    const [serviceStatus, setServiceStatus] = useState(null);
    const [isNew, setIsNew] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [creditData, subs, status] = await Promise.all([
                getCredits(user.id),
                getUserSubscriptions(user.id).catch(() => null),
                getUserServiceStatus(user.id).catch(() => null),
            ]);
            const pw = creditData?.personal_whatsapp || {};
            setCredits({
                pwc: pw.pwc != null ? String(pw.pwc) : "",
                dwpc: pw.dwpc != null ? String(pw.dwpc) : "",
                adwpc: pw.adwpc != null ? String(pw.adwpc) : "",
            });
            setSubscriptions(subs);
            setServiceStatus(status);
            const hasRole = parseUserRoles(user.user_role).includes("PERSONAL");
            setIsNew(!hasRole && !pw.pwc);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [user.id, user.user_role]);

    useEffect(() => { load(); }, [load]);

    const handleSave = async () => {
        setSaving(true);
        setError("");
        try {
            const payload = { service: "personal_whatsapp" };
            let hasField = false;
            ["pwc", "dwpc", "adwpc"].forEach((field) => {
                if (credits[field] !== "") {
                    payload[field] = parseInt(credits[field], 10);
                    hasField = true;
                }
            });
            if (!hasField) {
                setError("Enter at least one credit value.");
                return;
            }

            const hasPersonalRole = parseUserRoles(user.user_role).includes("PERSONAL");
            if (!hasPersonalRole) {
                const roles = [...parseUserRoles(user.user_role), "PERSONAL"];
                await updateUserRoles(user.id, roles, "replace");
            }

            await updateCredits(user.id, payload);
            notify("Personal WhatsApp service saved", "success");
            reloadUser();
            load();
        } catch (err) {
            setError(err.message);
            notify(err.message, "error");
        } finally {
            setSaving(false);
        }
    };

    const handleSubscriptionSave = async (service, payload) => {
        setSaving(true);
        try {
            await updateSubscription(user.id, service, payload);
            notify("Subscription updated", "success");
            setSubscriptions(await getUserSubscriptions(user.id));
        } catch (err) {
            notify(err.message, "error");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>;
    }

    return (
        <Paper elevation={0} sx={{ ...WB_SX.card, p: 3 }}>
            {isNew && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    This user does not have Personal WhatsApp set up yet. Enter credits and save to provision the service (PERSONAL role will be assigned automatically).
                </Alert>
            )}
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            {serviceStatus?.personal_whatsapp && (
                <Box sx={{ mb: 2 }}>
                    <HealthBadge healthy={serviceStatus.personal_whatsapp.is_healthy} />
                </Box>
            )}

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Credits</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {["pwc", "dwpc", "adwpc"].map((field) => (
                    <Grid item xs={12} sm={4} key={field}>
                        <TextField fullWidth size="small" label={field.toUpperCase()} type="number" value={credits[field]} onChange={(e) => setCredits((p) => ({ ...p, [field]: e.target.value }))} inputProps={{ min: 0 }} />
                    </Grid>
                ))}
            </Grid>

            <SubscriptionFields service="personal_whatsapp" sub={subscriptions?.personal_whatsapp} onSave={handleSubscriptionSave} saving={saving} />

            <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />} onClick={handleSave} disabled={saving} sx={{ ...WB_SX.primaryButton, mt: 3 }}>
                {isNew ? "Create & save service" : "Save credits"}
            </Button>
        </Paper>
    );
};

export default AdminPersonalWhatsappPage;
